import { BehaviorPartialSubject, createDivElement } from '@mbrtargeting/metatag-utils';

export enum AdLabelOrientation {
    horizontal = 'horizontal',
    verticalLeft = 'vertical-left',
    verticalRight = 'vertical-right',
}

export interface AdLabelConfig {
    hide: boolean;
    unfilled: boolean;
    text: string;
    political: boolean;
    disable: boolean;
    elementId: string,
    orientation: AdLabelOrientation;
    cssClassAdLabel: string;
    cssClassSlotName: string;
    cssClassHideAdLabel: string;
    cssClassDisableAdLabel: string;
    cssClassPoliticalAdLabel: string;
    cssClassOrientation: Record<AdLabelOrientation, string>;
}

/**
 * Some notes:
 *
 * AdLabel knows several states, which need to be considered for implementation.
 *
 * Allowed: Label is allowed by publisher, expressed by publisher intention (ex: in metaTag slot.configure())
 *          - when not allowed, render the AdLabel but don't show it, so it can be turned on during runtime (ex: when we need label for political ads).
 * Visibility: Label should
 *          - be visible when AdSlot is filled
 *          - not be visible when AdSlot is empty or used as a blocker
 *          - when AdSlot reservesSpaces, hiding the adLabel should not cause CLS
 */


export class AdLabel {

    #config$: BehaviorPartialSubject<AdLabelConfig>;
    readonly #element: HTMLElement;

    constructor(config: AdLabelConfig) {

        this.#config$ = new BehaviorPartialSubject<AdLabelConfig>(config);

        // make sure deconstructed variables stay separated and don't conflict with .subscribe()
        this.#element = (() => {
            const { elementId, cssClassAdLabel, orientation, cssClassOrientation } = this.#config$.value;
            return createDivElement({
                id: elementId,
                classList: [cssClassAdLabel, cssClassOrientation[orientation]],
            })
        })();

        this.#config$.subscribe(({ text, hide, unfilled, disable, political, orientation, cssClassOrientation, cssClassPoliticalAdLabel, cssClassDisableAdLabel, cssClassHideAdLabel }) => {
            const div = this.#element;

            // should not be visible (but render) when hidden (ex: slot blocked/empty). Required by law to always show when political ad renders
            if ((hide || unfilled) && !political) div.classList.add(cssClassHideAdLabel); else div.classList.remove(cssClassHideAdLabel);

            // should not render when removed by Publisher or config, but has to show for political ads
            if (disable && !hide && !political) div.classList.add(cssClassDisableAdLabel); else div.classList.remove(cssClassDisableAdLabel);

            if (political) div.classList.add(cssClassPoliticalAdLabel); else div.classList.remove(cssClassPoliticalAdLabel);

            if (text && div.getAttribute('data-adlabel') !== text) div.setAttribute('data-adlabel', text);

            if (!div.classList.contains(cssClassOrientation[orientation])) {

                div.classList.remove(...Object.values(cssClassOrientation));

                div.classList.add(cssClassOrientation[orientation]);
            }
        });
    }

    configure(newConfig: Partial<AdLabelConfig>) {
        this.#config$.next(newConfig);
    }

    get element() {
        return this.#element;
    }
}
